
import { BlogPost, FlotiqResponse } from "@/types/flotiq";

const FLOTIQ_API_KEY = "8ca9558e98467cdfb2c6b8ec1d702dbf";
const FLOTIQ_BASE_URL = "https://api.flotiq.com";

const headers = {
  "X-AUTH-TOKEN": FLOTIQ_API_KEY,
  "Content-Type": "application/json",
};

export async function getBlogPosts(): Promise<BlogPost[]> {
  try {
    // Request posts with sorting by publicationDate in descending order (newest first)
    const response = await fetch(`${FLOTIQ_BASE_URL}/api/v1/content/blogpost?order_by=publicationDate&order_direction=desc`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch blog posts: ${response.statusText}`);
    }

    const data: FlotiqResponse<BlogPost> = await response.json();
    
    // Log the first post to see its structure
    if (data.data.length > 0) {
      console.log('Sample blog post structure:', JSON.stringify(data.data[0], null, 2).substring(0, 1000));
      
      // Debug images specifically
      const firstPost = data.data[0];
      console.log('Featured image structure:', JSON.stringify(firstPost.featuredImage, null, 2));
      console.log('Header image structure:', JSON.stringify(firstPost.headerImage, null, 2));
    }
    
    return data.data;
  } catch (error) {
    console.error("Error fetching blog posts:", error);
    return [];
  }
}

export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  try {
    const response = await fetch(`${FLOTIQ_BASE_URL}/api/v1/content/blogpost?filters={"slug":{"type":"equals","filter":"${slug}"}}`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch blog post: ${response.statusText}`);
    }

    const data: FlotiqResponse<BlogPost> = await response.json();
    
    if (data.data.length > 0) {
      const post = data.data[0];
      
      // Log post content type and structure for debugging
      console.log('Post content type:', typeof post.content);
      if (typeof post.content === 'object') {
        console.log('Post content structure:', JSON.stringify(post.content, null, 2).substring(0, 500));
      }
      
      // Debug images specifically for this post
      console.log(`Debug for post ${slug}:`, {
        featuredImage: post.featuredImage,
        headerImage: post.headerImage
      });
      
      return post;
    }
    
    return null;
  } catch (error) {
    console.error(`Error fetching blog post with slug "${slug}":`, error);
    return null;
  }
}

export function getImageUrl(imageInput: string | any, width = 800): string {
  // Debug the image input to see what we're working with
  console.log('getImageUrl input:', imageInput);
  
  // If imageInput is null or undefined, return placeholder
  if (!imageInput) {
    console.log('No image input provided');
    return `${window.location.origin}/placeholder.svg`;
  }
  
  // Case for array with internal dataUrl (as seen in the logs)
  if (Array.isArray(imageInput) && imageInput.length > 0) {
    const item = imageInput[0];
    
    // Handle internal dataUrl format
    if (item && typeof item === 'object' && 'dataUrl' in item) {
      const dataUrl = item.dataUrl;
      if (dataUrl.includes('_media-')) {
        const matches = dataUrl.match(/_media-([a-zA-Z0-9-]+)/);
        if (matches && matches[1]) {
          const mediaId = `_media-${matches[1]}`;
          console.log(`Constructed media URL from array dataUrl: ${FLOTIQ_BASE_URL}/image/${width}/0/${mediaId}.webp`);
          return `${FLOTIQ_BASE_URL}/image/${width}/0/${mediaId}.webp`;
        }
      }
      // Direct use of dataUrl if it has full URL
      if (dataUrl.startsWith('http')) {
        return dataUrl;
      } else {
        return `${FLOTIQ_BASE_URL}${dataUrl}`;
      }
    }
    
    // Handle type and dataUrl format
    if (item && typeof item === 'object' && 'type' in item && 'dataUrl' in item) {
      const dataUrl = item.dataUrl;
      if (dataUrl.includes('_media-')) {
        const matches = dataUrl.match(/_media-([a-zA-Z0-9-]+)/);
        if (matches && matches[1]) {
          const mediaId = `_media-${matches[1]}`;
          console.log(`Constructed media URL from type/dataUrl: ${FLOTIQ_BASE_URL}/image/${width}/0/${mediaId}.webp`);
          return `${FLOTIQ_BASE_URL}/image/${width}/0/${mediaId}.webp`;
        }
      }
      return `${FLOTIQ_BASE_URL}${dataUrl}`;
    }
    
    // If array item has url property
    if (item && typeof item === 'object' && 'url' in item) {
      const url = item.url;
      if (url.startsWith('http')) {
        return url;
      }
      return `${FLOTIQ_BASE_URL}${url}`;
    }
    
    // If array item has id property
    if (item && typeof item === 'object' && 'id' in item) {
      return `${FLOTIQ_BASE_URL}/image/${width}/0/${item.id}.webp`;
    }
  }
  
  // Case 1: imageInput is a string (ID or URL)
  if (typeof imageInput === 'string') {
    // If it's a full URL, return as is
    if (imageInput.startsWith('http')) {
      return imageInput;
    }
    
    // If it's a relative URL, add base URL
    if (imageInput.startsWith('/')) {
      return `${FLOTIQ_BASE_URL}${imageInput}`;
    }
    
    // Handle ID extraction from paths
    let imageId = imageInput;
    if (imageInput.includes('_media-')) {
      const matches = imageInput.match(/_media-([a-zA-Z0-9-]+)/);
      if (matches && matches[1]) {
        imageId = `_media-${matches[1]}`;
      }
    }
    
    return `${FLOTIQ_BASE_URL}/image/${width}/0/${imageId}.webp`;
  }
  
  // Case 2: imageInput is an object with url property
  if (imageInput && typeof imageInput === 'object' && 'url' in imageInput) {
    const url = imageInput.url;
    if (url.startsWith('http')) {
      return url;
    }
    return `${FLOTIQ_BASE_URL}${url}`;
  }
  
  // Case 3: imageInput is an object with id property
  if (imageInput && typeof imageInput === 'object' && 'id' in imageInput) {
    return `${FLOTIQ_BASE_URL}/image/${width}/0/${imageInput.id}.webp`;
  }
  
  // Fallback to placeholder if we can't determine image URL
  console.warn('Could not determine image URL from input:', imageInput);
  return `${window.location.origin}/placeholder.svg`;
}

export async function getAllBlogSlugs(): Promise<string[]> {
  try {
    const posts = await getBlogPosts();
    return posts.map(post => post.slug);
  } catch (error) {
    console.error("Error fetching blog slugs:", error);
    return [];
  }
}
